import React, { useState, useCallback, useRef } from 'react';
import { useColumnPreferences } from '../../context/ColumnPreferencesContext';
import Modal from '../Modal';
import ConfirmDialog from '../ConfirmDialog';
import { Search, GripVertical, RotateCcw, Eye, EyeOff } from 'lucide-react';
import './ColumnConfigurator.css';

function ColumnConfiguratorModal({ isOpen, onClose, entityType }) {
  const {
    getAllColumns,
    toggleColumnVisibility,
    reorderColumns,
    resetColumns,
    getVisibleColumns,
  } = useColumnPreferences();

  const [searchQuery, setSearchQuery] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragCounter = useRef(0);

  const allColumns = getAllColumns(entityType);
  const visibleColumns = getVisibleColumns(entityType);
  const visibleCount = visibleColumns.length;
  const totalCount = allColumns.length;

  // Filter columns by search
  const filteredColumns = allColumns.filter(col =>
    col.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = useCallback((key) => {
    toggleColumnVisibility(entityType, key);
  }, [entityType, toggleColumnVisibility]);

  const handleSelectAll = useCallback(() => {
    const updated = allColumns.map(col => ({
      ...col,
      visible: true,
    }));
    reorderColumns(entityType, updated);
  }, [allColumns, entityType, reorderColumns]);

  const handleDeselectAll = useCallback(() => {
    const updated = allColumns.map(col => ({
      ...col,
      visible: col.locked ? true : false,
    }));
    reorderColumns(entityType, updated);
  }, [allColumns, entityType, reorderColumns]);

  const handleReset = useCallback(async () => {
    await resetColumns(entityType);
    setShowResetConfirm(false);
  }, [entityType, resetColumns]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Add a slight delay before adding the dragging class
    requestAnimationFrame(() => {
      e.target.classList.add('column-item-dragging');
    });
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.classList.remove('column-item-dragging');
    setDragIndex(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  }, []);

  const handleDragEnter = useCallback((e, index) => {
    e.preventDefault();
    dragCounter.current++;
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverIndex(null);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOverIndex(null);

    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      return;
    }

    // Reorder: move dragged item to drop position
    const updated = [...allColumns];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, moved);
    reorderColumns(entityType, updated);
    setDragIndex(null);
  }, [dragIndex, allColumns, entityType, reorderColumns]);

  // Keyboard reordering
  const handleKeyDown = useCallback((e, index) => {
    if (e.key === 'ArrowUp' && index > 0) {
      e.preventDefault();
      const updated = [...allColumns];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      reorderColumns(entityType, updated);
      // Focus the moved item
      requestAnimationFrame(() => {
        const items = document.querySelectorAll('.column-item-drag-handle');
        if (items[index - 1]) items[index - 1].focus();
      });
    } else if (e.key === 'ArrowDown' && index < allColumns.length - 1) {
      e.preventDefault();
      const updated = [...allColumns];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      reorderColumns(entityType, updated);
      requestAnimationFrame(() => {
        const items = document.querySelectorAll('.column-item-drag-handle');
        if (items[index + 1]) items[index + 1].focus();
      });
    }
  }, [allColumns, entityType, reorderColumns]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} title="Configure Columns" size="md">
        <div className="column-configurator">
          {/* Search */}
          <div className="column-configurator-search">
            <Search size={16} className="column-configurator-search-icon" />
            <input
              type="text"
              className="form-input"
              placeholder="Search columns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>

          {/* Actions bar */}
          <div className="column-configurator-actions">
            <span className="column-configurator-count">
              {visibleCount} of {totalCount} columns visible
            </span>
            <div className="column-configurator-buttons">
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleSelectAll}
                title="Show all columns"
              >
                <Eye size={14} />
                All
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleDeselectAll}
                title="Hide optional columns"
              >
                <EyeOff size={14} />
                None
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowResetConfirm(true)}
                title="Reset to defaults"
              >
                <RotateCcw size={14} />
                Reset
              </button>
            </div>
          </div>

          {/* Column list */}
          <div className="column-configurator-list" role="listbox" aria-label="Column configuration">
            {filteredColumns.map((col, index) => {
              // Find the real index in allColumns (for drag/drop)
              const realIndex = allColumns.findIndex(c => c.key === col.key);
              const isSearching = searchQuery.length > 0;

              return (
                <div
                  key={col.key}
                  className={`column-item ${dragOverIndex === realIndex ? 'column-item-drag-over' : ''} ${col.locked ? 'column-item-locked' : ''}`}
                  draggable={!isSearching}
                  onDragStart={(e) => handleDragStart(e, realIndex)}
                  onDragEnd={handleDragEnd}
                  onDragEnter={(e) => handleDragEnter(e, realIndex)}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, realIndex)}
                  role="option"
                  aria-selected={col.visible}
                >
                  {!isSearching && (
                    <span
                      className="column-item-drag-handle"
                      tabIndex={0}
                      onKeyDown={(e) => handleKeyDown(e, realIndex)}
                      aria-label={`Reorder ${col.label}. Use arrow keys to move.`}
                    >
                      <GripVertical size={14} />
                    </span>
                  )}
                  <label className="column-item-label">
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={col.visible}
                      disabled={col.locked}
                      onChange={() => handleToggle(col.key)}
                      aria-label={`${col.visible ? 'Hide' : 'Show'} ${col.label} column`}
                    />
                    <span className="column-item-text">{col.label}</span>
                    {col.locked && <span className="column-item-lock-badge">Required</span>}
                  </label>
                </div>
              );
            })}
            {filteredColumns.length === 0 && (
              <div className="column-configurator-empty">
                No columns match "{searchQuery}"
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleReset}
        title="Reset Columns"
        message="Reset all column visibility and ordering to defaults? Your custom configuration will be lost."
        confirmText="Reset"
      />
    </>
  );
}

export default ColumnConfiguratorModal;
